import React, { useEffect, useState, useCallback } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { BookPlus, Trash2, Pencil, Upload, Megaphone, Layers, Sparkles, Wand2 } from "lucide-react";
import MarketingTabs from "@/pages/admin/MarketingTabs";
import CollectionsTab from "@/pages/admin/CollectionsTab";

const initialForm = {
  title: "", author: "", description: "", price: "",
  category: "Literary Fiction", pages: "", cover_path: "", pdf_path: "", preview_path: "",
  shop_owned: true, external_url: "",
};

export default function Admin() {
  const [tab, setTab] = useState("books");
  const [stats, setStats] = useState(null);
  const [books, setBooks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState({ cover: false, pdf: false });
  const [notify, setNotify] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, b, o] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/books"),
        api.get("/admin/orders"),
      ]);
      setStats(s.data); setBooks(b.data); setOrders(o.data);
    } catch (err) {
      console.error("Failed to load admin data", err);
      toast.error("Failed to load admin data");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async (file, kind) => {
    const maxBytes = kind === "pdf" ? 25 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxBytes) {
      const limit = kind === "pdf" ? "25 MB" : "5 MB";
      toast.error(`${kind === "pdf" ? "PDF" : "Image"} too large. Max allowed is ${limit}.`);
      return;
    }
    setUploading((u) => ({ ...u, [kind]: true }));
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/upload/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (kind === "cover") {
        setForm((f) => ({ ...f, cover_path: data.path }));
      } else {
        setForm((f) => ({ ...f, pdf_path: data.path, preview_path: data.preview_path || "" }));
      }
      toast.success(`${kind === "cover" ? "Cover" : "PDF"} uploaded`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading((u) => ({ ...u, [kind]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: parseFloat(form.price),
      pages: parseInt(form.pages || 0, 10),
    };
    try {
      if (editingId) {
        await api.put(`/books/${editingId}`, payload);
        toast.success("Book updated");
      } else {
        await api.post("/books", payload, { params: { notify_subscribers: notify } });
        toast.success(notify ? "Book added — subscribers notified" : "Book added to catalog");
      }
      setForm(initialForm); setEditingId(null); setNotify(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    }
  };

  const handleEdit = (b) => {
    setEditingId(b.id);
    setForm({
      title: b.title, author: b.author, description: b.description,
      price: String(b.price), category: b.category, pages: String(b.pages || 0),
      cover_path: b.cover_path || "", pdf_path: b.pdf_path || "",
      preview_path: b.preview_path || "",
      shop_owned: b.shop_owned !== false,
      external_url: b.external_url || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this book?")) return;
    await api.delete(`/books/${id}`);
    toast.success("Book deleted");
    load();
  };

  return (
    <div data-testid="admin-page" className="max-w-7xl mx-auto px-6 md:px-12 py-12 font-sans">
      <div className="flex items-center justify-between border-b border-edge pb-6 mb-10">
        <div>
          <div className="label-eyebrow text-sage">Folio</div>
          <h1 className="font-serif text-4xl tracking-tight">Admin dashboard.</h1>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
          {[
            { l: "Books", v: stats.total_books, tid: "stat-books" },
            { l: "Readers", v: stats.total_users, tid: "stat-readers" },
            { l: "Subscribers", v: stats.total_subscribers ?? 0, tid: "stat-subscribers" },
            { l: "Sales", v: stats.total_sales, tid: "stat-sales" },
            { l: "Revenue", v: `$${stats.revenue}`, tid: "stat-revenue" },
            {
              l: `${stats.month_label || "This Month"}`,
              v: `$${stats.monthly_revenue ?? 0}`,
              sub: `${stats.monthly_sales ?? 0} sale${(stats.monthly_sales ?? 0) === 1 ? "" : "s"}`,
              accent: true,
              tid: "stat-monthly-revenue",
            },
          ].map((s) => (
            <div key={s.l} data-testid={s.tid}
                 className={`border p-6 ${s.accent ? "border-terracotta bg-linen" : "border-edge bg-white"}`}>
              <div className="label-eyebrow">{s.l}</div>
              <div className="font-serif text-4xl mt-2">{s.v}</div>
              {s.sub && <div className="text-xs text-muted_ink mt-1">{s.sub}</div>}
            </div>
          ))}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent gap-2 p-0">
          <TabsTrigger value="books" data-testid="tab-books" className="rounded-none data-[state=active]:bg-ink data-[state=active]:text-paper">Books</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-none data-[state=active]:bg-ink data-[state=active]:text-paper">Orders</TabsTrigger>
          <TabsTrigger value="collections" data-testid="tab-collections" className="rounded-none data-[state=active]:bg-ink data-[state=active]:text-paper">
            <Layers className="w-3.5 h-3.5 mr-2" />Collections
          </TabsTrigger>
          <TabsTrigger value="marketing" data-testid="tab-marketing" className="rounded-none data-[state=active]:bg-ink data-[state=active]:text-paper">
            <Megaphone className="w-3.5 h-3.5 mr-2" />Marketing
          </TabsTrigger>
        </TabsList>

        <TabsContent value="books" className="mt-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Form */}
            <form onSubmit={handleSubmit} className="lg:col-span-1 bg-white border border-edge p-6 space-y-4">
              <div className="flex items-center gap-2 font-serif text-2xl"><BookPlus className="w-5 h-5 text-terracotta" />{editingId ? "Edit book" : "Add a book"}</div>

              <div><label className="label-eyebrow block mb-1">Title</label>
                <Input data-testid="admin-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none border-edge" /></div>
              <div><label className="label-eyebrow block mb-1">Author</label>
                <Input data-testid="admin-author-input" required value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className="rounded-none border-edge" /></div>
              <div><label className="label-eyebrow block mb-1">Description</label>
                <Textarea data-testid="admin-desc-input" required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none border-edge" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label-eyebrow block mb-1">Price ($)</label>
                  <Input data-testid="admin-price-input" required type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-none border-edge" /></div>
                <div><label className="label-eyebrow block mb-1">Pages</label>
                  <Input type="number" value={form.pages} onChange={(e) => setForm({ ...form, pages: e.target.value })} className="rounded-none border-edge" /></div>
              </div>
              <div><label className="label-eyebrow block mb-1">Category</label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="admin-category-select" className="rounded-none border-edge"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Literary Fiction","Essays","Poetry","Short Stories","Historical Fiction","Business","Non-Fiction","Memoir"].map(c =>
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    )}
                  </SelectContent>
                </Select></div>

              <div>
                <label className="label-eyebrow block mb-1">Cover image</label>
                <label className="flex items-center gap-2 border border-dashed border-edge p-3 cursor-pointer hover:bg-linen">
                  <Upload className="w-4 h-4" />
                  <span className="text-sm text-muted_ink">{uploading.cover ? "Uploading…" : form.cover_path ? "Replace cover" : "Upload cover (jpg/png)"}</span>
                  <input data-testid="admin-cover-upload" type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], "cover")} />
                </label>
                {form.cover_path && <div className="text-xs text-sage mt-1 truncate">{form.cover_path}</div>}
              </div>

              <div>
                <label className="label-eyebrow block mb-1">PDF file</label>
                <label className="flex items-center gap-2 border border-dashed border-edge p-3 cursor-pointer hover:bg-linen">
                  <Upload className="w-4 h-4" />
                  <span className="text-sm text-muted_ink">{uploading.pdf ? "Uploading…" : form.pdf_path ? "Replace PDF" : "Upload ebook (PDF)"}</span>
                  <input data-testid="admin-pdf-upload" type="file" accept="application/pdf" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], "pdf")} />
                </label>
                {form.pdf_path && <div className="text-xs text-sage mt-1 truncate">{form.pdf_path}</div>}
                {form.preview_path && <div className="text-xs text-sage mt-1 truncate">3-page preview auto-generated ✓</div>}
              </div>

              <div>
                <label className="label-eyebrow block mb-1">External read-for-free URL (optional)</label>
                <Input
                  data-testid="admin-external-url"
                  value={form.external_url}
                  onChange={(e) => setForm({ ...form, external_url: e.target.value })}
                  className="rounded-none border-edge"
                  placeholder="https://www.gutenberg.org/ebooks/..."
                />
                <div className="text-xs text-muted_ink mt-1">
                  For public-domain classics — shown as "also freely available at…" on the book page.
                </div>
              </div>

              <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted_ink cursor-pointer">
                <input
                  data-testid="admin-shop-owned"
                  type="checkbox"
                  checked={form.shop_owned}
                  onChange={(e) => setForm({ ...form, shop_owned: e.target.checked })}
                  className="accent-terracotta"
                />
                Show "A Folio Edition" badge
              </label>

              <div className="flex gap-2 pt-2">
                <button data-testid="admin-save-btn" type="submit" className="btn-primary flex-1">{editingId ? "Update" : "Add to catalog"}</button>
                {editingId && (
                  <button type="button" onClick={() => { setForm(initialForm); setEditingId(null); }} className="btn-secondary">Cancel</button>
                )}
              </div>

              {!editingId && (
                <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted_ink cursor-pointer">
                  <input
                    data-testid="notify-subscribers"
                    type="checkbox"
                    checked={notify}
                    onChange={(e) => setNotify(e.target.checked)}
                    className="accent-terracotta"
                  />
                  Notify subscribers about this release
                </label>
              )}
            </form>

            {/* Books list */}
            <div className="lg:col-span-2">
              <BackfillPlaceholderPdfsCard />
              <div className="border border-edge bg-white divide-y divide-edge">
                {books.length === 0 && <div className="p-6 text-muted_ink">No books yet.</div>}
                {books.map((b) => (
                  <div key={b.id} data-testid={`admin-book-row-${b.id}`} className="p-4 flex items-center gap-4">
                    <div className="w-14 aspect-[3/4] bg-linen flex-shrink-0 overflow-hidden">
                      {(b.cover_url || b.cover_path) && (
                        <img src={b.cover_url || `${API}/files/${b.cover_path}`} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-lg truncate">{b.title}</div>
                      <div className="text-xs text-muted_ink">{b.author} · {b.category}</div>
                    </div>
                    <div className="text-sm font-medium">${b.price?.toFixed(2)}</div>
                    <button data-testid={`edit-book-${b.id}`} onClick={() => handleEdit(b)} className="p-2 hover:text-terracotta"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`delete-book-${b.id}`} onClick={() => handleDelete(b.id)} className="p-2 hover:text-terracotta"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="orders" className="mt-8">
          <div className="border border-edge bg-white">
            <div className="grid grid-cols-12 px-4 py-3 label-eyebrow border-b border-edge">
              <div className="col-span-4">Customer</div>
              <div className="col-span-3">Book</div>
              <div className="col-span-2">Amount</div>
              <div className="col-span-3">Status</div>
            </div>
            {orders.length === 0 && <div className="p-6 text-muted_ink">No orders yet.</div>}
            {orders.map((o) => (
              <div key={o.id} className="grid grid-cols-12 px-4 py-3 border-b border-edge text-sm">
                <div className="col-span-4 truncate">{o.user_email}</div>
                <div className="col-span-3 truncate">{o.metadata?.book_title || o.book_id}</div>
                <div className="col-span-2">${o.amount?.toFixed(2)}</div>
                <div className="col-span-3">
                  <span className={`inline-block px-2 py-0.5 text-xs uppercase tracking-widest ${
                    o.payment_status === "paid" ? "bg-sage/20 text-sage" :
                    o.payment_status === "initiated" ? "bg-linen text-muted_ink" :
                    "bg-terracotta/10 text-terracotta"
                  }`}>{o.payment_status}</span>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="collections" className="mt-8">
          <CollectionsTab />
        </TabsContent>

        <TabsContent value="marketing" className="mt-8">
          <MarketingTabs />
        </TabsContent>
      </Tabs>
    </div>
  );
}



// ---------------------------------------------------------------------------
// Backfill placeholder PDFs — one-click generator for books missing a PDF.
// ---------------------------------------------------------------------------
function BackfillPlaceholderPdfsCard() {
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const run = async (force) => {
    if (running) return;
    if (force && !window.confirm(
      "Force-regenerate PDFs for EVERY book, including books that already have a PDF?\n\n" +
      "This will overwrite existing uploaded manuscripts with placeholder editions."
    )) return;

    setRunning(true);
    setLastResult(null);
    try {
      const { data } = await api.post(
        `/admin/books/backfill-pdfs${force ? "?force=true" : ""}`,
        {},
        { timeout: 180000 } // 3 min ceiling for 30+ books
      );
      setLastResult(data);
      if ((data.failed || []).length === 0) {
        toast.success(`Backfilled ${data.updated}/${data.total} books`);
      } else {
        toast.warning(`${data.updated} succeeded, ${data.failed.length} failed`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e, "Backfill failed"));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      data-testid="admin-backfill-pdfs-card"
      className="mb-6 border border-edge bg-white p-5"
    >
      <div className="flex items-start gap-3">
        <Wand2 className="w-5 h-5 text-terracotta flex-shrink-0 mt-1" strokeWidth={1.5} />
        <div className="flex-1 min-w-0">
          <div className="font-serif text-lg text-ink">Backfill placeholder PDFs</div>
          <p className="text-sm text-muted_ink mt-1 leading-relaxed">
            Generates a 30-page "Folio Press edition" PDF (title page, editorial
            note using each book's description, sample chapters, colophon) for
            any book that has no PDF attached. Skips books that already have one.
            Use this once to make every book downloadable, then replace with
            real manuscripts via the form on the left.
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            <button
              type="button"
              data-testid="admin-backfill-run-btn"
              onClick={() => run(false)}
              disabled={running}
              className="btn-primary disabled:opacity-60 !px-4 !py-2 text-xs"
            >
              {running ? "Running…" : "Backfill missing"}
            </button>
            <button
              type="button"
              data-testid="admin-backfill-force-btn"
              onClick={() => run(true)}
              disabled={running}
              className="btn-secondary disabled:opacity-60 !px-4 !py-2 text-xs"
            >
              Force-regenerate all
            </button>
          </div>
          {lastResult && (
            <div
              data-testid="admin-backfill-result"
              className="mt-4 text-xs text-muted_ink border-t border-edge pt-3 space-y-1"
            >
              <div>
                <span className="text-sage font-medium">{lastResult.updated}</span>
                {" "}of{" "}
                <span className="text-ink">{lastResult.total}</span>{" "}books updated
                {(lastResult.failed || []).length > 0 && (
                  <>
                    {" · "}
                    <span className="text-terracotta font-medium">
                      {lastResult.failed.length} failed
                    </span>
                  </>
                )}
              </div>
              {(lastResult.failed || []).slice(0, 5).map((f) => (
                <div key={f} className="text-terracotta">· {f}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
